/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). The four bid responses to
 * RFP-2026-014 plus the RFP requirements, authored as documents for the Ask
 * panel. Numbers match BIDS in sourcing-data.ts (the tests check the unit
 * prices). Supplier names, contacts and D-U-N-S numbers come from the supplier
 * master. Each bid has deliberately negotiable or superfluous items so the
 * scripted questions have something real to find.
 */
import type { AskDocument } from '../../ask/types';
import { getSupplier } from '../../data/suppliers';
import type { BidderId } from './sourcing-types';

export const RFP_DOC_ID = 'rfp-2026-014';
export const BID_DOC_IDS = {
  'sup-aurelia': 'bid-aurelia',
  'sup-northgate': 'bid-northgate',
  'sup-hanover': 'bid-hanover',
  'sup-brightpak': 'bid-brightpak',
} as const;

/** Contact name and title line from the supplier master. */
function contactLine(id: BidderId): string {
  const { name, title } = getSupplier(id).primaryContact;
  return `${name}, ${title}`;
}

const RFP_DOCUMENT: AskDocument = {
  id: RFP_DOC_ID,
  title: 'RFP-2026-014 requirements and evaluation terms',
  shortTitle: 'Bid request',
  sections: [
    {
      ref: '1',
      heading: 'Scope and volume',
      text: 'Supply of Pillar Pro LED high-bay fixtures, 150 W, 5000K, 120 to 277 V with 0 to 10 V dimming and the Pillar Pro nameplate, for stocking at the Dallas DC (DAL), Phoenix DC (PHX) and Hayward DC (HAY). Estimated volume is 130,000 fixtures per year for 2027 to 2029. Bidders may offer 100% of volume or a defined share. Pillar intends to keep at least two approved suppliers.',
    },
    {
      ref: '2',
      heading: 'Pricing instructions',
      text: 'Quote per fixture delivered to DAL, PHX and HAY. Show unit price, freight, and any duties or tariffs as separate lines. All fees, surcharges and upcharges must be listed in the pricing schedule; charges that are not listed are not payable. Bids are evaluated on delivered cost, which is unit price plus freight plus tariff and duty.',
    },
    {
      ref: '3',
      heading: 'Payment and commercial terms',
      text: 'Pillar standard payment terms are Net 60 from receipt of a valid invoice. Any deviation must be listed as an exception and is scored under commercial terms. Bids must remain valid for at least 120 days after the due date. Bidders should state how long prices are held and any mechanism that adjusts them with market prices.',
    },
    {
      ref: '4',
      heading: 'Quality and certification requirements',
      text: 'Mandatory: UL certification and DLC Premium qualification for the quoted 150 W, 5000K model, with the UL certificate number supplied. Mandatory: ISO 9001 certification. Mandatory: at least 180 days of prior written notice before any change of manufacturing site, LED package, driver or housing material, consistent with Pillar quality agreements and the UL certification. Mandatory: a light lifetime (hours until output falls to 70%) of at least 100,000 hours, tested to industry standards. Preferred: a 10-year warranty, with 5 years as the minimum.',
    },
    {
      ref: '5',
      heading: 'Supply security',
      text: 'Bidders declare annual capacity, lead time and minimum order quantity. Pillar caps any single supplier at 60% of a critical direct material to avoid depending on one supplier. Bidders must be able to serve the Dallas, Phoenix and Hayward DCs.',
    },
    {
      ref: '6',
      heading: 'Trade compliance and forced-labor rules',
      text: 'Bidders must attest that no input is mined, produced or manufactured wholly or in part in the Xinjiang Uyghur Autonomous Region or by an entity on the US forced-labor import ban list, and must name the suppliers behind its aluminium housing and heat-sink suppliers on request. Tariffs and duties for the importer are included in the delivered cost used for evaluation.',
    },
    {
      ref: '7',
      heading: 'Sustainability',
      text: 'Bidders describe recyclable packaging, including any foam-free roadmap, and recycled aluminium content in the housing. Sustainability is scored on the sustainability rating (EcoVadis), recycled content and the credibility of the roadmap.',
    },
    {
      ref: '8',
      heading: 'Evaluation weights',
      text: 'Delivered cost 35, quality and certifications 20, supply security and capacity 15, lead time 10, sustainability 10, commercial terms 10. Scores run from 0 to 10 per criterion.',
    },
  ],
};

const AURELIA_BID: AskDocument = {
  id: BID_DOC_IDS['sup-aurelia'],
  title: `${getSupplier('sup-aurelia').name} bid response`,
  shortTitle: 'Aurelia bid',
  sections: [
    {
      ref: '1',
      heading: 'Commercial offer',
      text: 'Aurelia offers to supply 100% of Pillar requirements, or any share from 50% upward, for 2027 to 2029 as the incumbent supplier. Prices are held for three years subject to the aluminium price clause in section 7. Aurelia proposes to continue the 2% early-payment discount (if paid within 10 days) from the current agreement.',
    },
    {
      ref: '2',
      heading: 'Pricing schedule',
      text: 'Unit price $83.70 per fixture, delivered, freight included, to the Dallas, Phoenix and Hayward DCs. Freight is included in the unit price, and the Mexican origin qualifies for duty-free entry under the North American trade agreement (USMCA), so no duties apply at current rates. The Pillar Pro nameplate, UL label and date-code marking are included. There are no set-up fees, and up to four private-label nameplate or carton artwork changes per year are free of charge. No optional fees are quoted.',
    },
    {
      ref: '3',
      heading: 'Logistics',
      text: 'Lead time is 6 weeks from purchase order. Minimum order quantity is 2,500 fixtures per release. All three DCs are served by cross-border truck through Laredo, with a buffer stock of 3 weeks held at a bonded warehouse in Laredo, Texas.',
    },
    {
      ref: '4',
      heading: 'Quality and certifications',
      text: 'UL certified and DLC Premium qualified for the 150 W, 5000K model, with the UL certificate number available on request. ISO 9001:2015, ISO 14001 and ISO 50001 certified at Monterrey. Quality on Pillar lots was 95 defects per million and 97% on time and in full over the last 12 months. The light lifetime (hours until output falls to 70%) is 150,000 hours, tested to industry standards, with a 10-year warranty. Aurelia commits to 180 days of written notice before any change of site, LED package, driver or housing material and will keep the UL certification current.',
    },
    {
      ref: '5',
      heading: 'Capacity',
      text: 'Installed capacity dedicated to Pillar Pro is 150,000 fixtures per year across assembly lines 1 to 3. Line 4 commissions in the second quarter of 2027 and adds 40,000 fixtures. Aurelia currently allocates 88,400 fixtures (68% of Pillar volume) and can support up to 100% of requirements.',
    },
    {
      ref: '6',
      heading: 'Sustainability',
      text: 'Sustainability rating (EcoVadis) 82. Foam-free corrugated cartons by 2028 under the recyclable packaging roadmap in the current agreement, 25% recycled aluminium housings from 2027, and the Monterrey plant runs on 100% renewable electricity under a power purchase agreement.',
    },
    {
      ref: '7',
      heading: 'Terms and exceptions',
      text: 'Payment Net 60 with a 2% early-payment discount if paid within 10 days. Price validity 180 days. Prices are held for three years with an aluminium price clause: if the LME aluminium price moves more than 8% from the reference of $2,480 per tonne, the aluminium-linked share of the price (about 24%) is adjusted quarterly, with no cap stated on the adjustment. Aurelia requests a minimum volume commitment of 50% of Pillar requirements for the term. No exceptions to Pillar quality terms.',
    },
    {
      ref: '8',
      heading: 'Company information',
      text: `${getSupplier('sup-aurelia').name}, Monterrey, Mexico. Supplier to Pillar since ${getSupplier('sup-aurelia').supplierSince ?? 2015} and a strategic supplier. Commercial contact: ${contactLine('sup-aurelia')}. Business ID (D-U-N-S) ${getSupplier('sup-aurelia').duns}.`,
    },
  ],
};

const NORTHGATE_BID: AskDocument = {
  id: BID_DOC_IDS['sup-northgate'],
  title: `${getSupplier('sup-northgate').name} bid response`,
  shortTitle: 'Northgate bid',
  sections: [
    {
      ref: '1',
      heading: 'Commercial offer',
      text: 'Northgate offers to supply 20% to 40% of Pillar volume for 2027 to 2029 from Akron, Ohio. Capacity limits supply to 40%. A dedicated account team is included in the offer and priced in section 2. The offer renews automatically for successive 12-month periods unless either party gives 90 days of notice.',
    },
    {
      ref: '2',
      heading: 'Pricing schedule',
      text: 'Unit price $85.03 per fixture, delivered, freight included, to the Dallas, Phoenix and Hayward DCs. Schedule of charges: (1) fixture unit price, $85.03; (2) dedicated account team, $48,000 per year billed quarterly, required for the service levels in Schedule C; (3) finish upcharge, $1.40 per fixture for any non-standard housing colour and $2.60 for a marine-grade coating; (4) rush production under 3 weeks, plus 8%; (5) nameplate or carton artwork change, $1,800 per change.',
    },
    {
      ref: '3',
      heading: 'Logistics',
      text: 'Lead time is 4 weeks. Minimum order quantity is 1,000 fixtures per release. The Dallas DC is served by truck in two days. The Phoenix and Hayward DCs are served by consolidated truckload with 4 to 6 days of transit, included in the price.',
    },
    {
      ref: '4',
      heading: 'Quality and certifications',
      text: 'UL certified and DLC Premium qualified for the 150 W, 5000K model, and ISO 9001:2015 certified. Quality on Pillar lots was 160 defects per million and 93% on time and in full over the last 12 months. The light lifetime (hours until output falls to 70%) is 100,000 hours with a 5-year warranty. Northgate offers 120 days of written notice before changes of site, LED package, driver or housing material. Light output and lifetime test reports are available on request within 20 business days.',
    },
    {
      ref: '5',
      heading: 'Capacity',
      text: 'Installed capacity dedicated to the high-bay line is 52,000 fixtures per year, which supports up to 40% of Pillar volume. Expansion beyond 40% is not available before 2029.',
    },
    {
      ref: '6',
      heading: 'Sustainability',
      text: 'Sustainability rating (EcoVadis) 61. No foam-free packaging roadmap at this time. Recycled aluminium content of 10% in housings from 2028. The Akron plant runs on grid power with a 22% renewable share.',
    },
    {
      ref: '7',
      heading: 'Terms and exceptions',
      text: 'Payment Net 30. Northgate does not accept Net 60 on new agreements. Price validity 120 days. Prices are held for two years, and the third year adjusts with published aluminium producer prices, capped at 3%. Exceptions to Pillar terms: payment terms, 120-day notice before changes and automatic renewal.',
    },
    {
      ref: '8',
      heading: 'Company information',
      text: `${getSupplier('sup-northgate').name}, Akron, Ohio. Supplier to Pillar since ${getSupplier('sup-northgate').supplierSince ?? 2018}. Commercial contact: ${contactLine('sup-northgate')}. Business ID (D-U-N-S) ${getSupplier('sup-northgate').duns}.`,
    },
  ],
};

const HANOVER_BID: AskDocument = {
  id: BID_DOC_IDS['sup-hanover'],
  title: `${getSupplier('sup-hanover').name} bid response`,
  shortTitle: 'Hanover bid',
  sections: [
    {
      ref: '1',
      heading: 'Commercial offer',
      text: "Hanover offers to supply 20% to 30% of Pillar volume as a new supplier from Hanover, Germany. This is Hanover's first bid for Pillar, and supplier onboarding with Pillar has started. Hanover asks for a minimum annual volume commitment of 25% of Pillar requirements.",
    },
    {
      ref: '2',
      heading: 'Pricing schedule',
      text: 'Unit price $86.70 per fixture. Delivery to the Dallas, Phoenix and Hayward DCs is quoted at $2.15 per fixture on a delivered basis, which includes duties and customs brokerage. First-article samples and photometric testing for the 150 W, 5000K model are borne by Hanover. No other fees are quoted.',
    },
    {
      ref: '3',
      heading: 'Logistics',
      text: 'Lead time is 10 weeks from purchase order, and first-article approval adds about 8 weeks during 2027. Minimum order quantity is 5,000 fixtures per release. Delivery is by consolidated ocean freight to Houston with onward truck.',
    },
    {
      ref: '4',
      heading: 'Quality and certifications',
      text: 'UL certified and DLC Premium qualified for the 150 W, 5000K model, with ISO 9001:2015 and ISO 14001. The light lifetime (hours until output falls to 70%) is 120,000 hours with a 10-year warranty. Hanover commits to 180 days of written notice before any change of site, LED package, driver or housing material. There is no quality history with Pillar because Hanover is a new supplier.',
    },
    {
      ref: '5',
      heading: 'Capacity',
      text: 'Dedicated capacity is 64,000 fixtures per year from line 7, installed in 2024, expandable to 90,000 by 2028.',
    },
    {
      ref: '6',
      heading: 'Sustainability',
      text: 'Sustainability rating (EcoVadis) 67. A foam-free packaging pilot is available for qualification in 2027, recycled aluminium content in housings is 30%, and the site uses certified green electricity.',
    },
    {
      ref: '7',
      heading: 'Terms and exceptions',
      text: 'Payment Net 45. Price validity 90 days. Prices are held for one year, followed by an annual review against market prices. Exceptions to Pillar terms: payment terms, price validity and a one-year price hold.',
    },
    {
      ref: '8',
      heading: 'Company information',
      text: `${getSupplier('sup-hanover').name}, Hanover, Germany. Family-owned commercial and industrial lighting manufacturer. Commercial contact: ${contactLine('sup-hanover')}. Business ID (D-U-N-S) ${getSupplier('sup-hanover').duns}.`,
    },
  ],
};

const BRIGHTPAK_BID: AskDocument = {
  id: BID_DOC_IDS['sup-brightpak'],
  title: `${getSupplier('sup-brightpak').name} bid response`,
  shortTitle: 'Brightpak bid',
  sections: [
    {
      ref: '1',
      heading: 'Commercial offer',
      text: 'Brightpak offers to supply up to 100% of Pillar volume at the lowest unit price among bidders. The offer is made on terms where Pillar collects the goods in Shenzhen and is the importer.',
    },
    {
      ref: '2',
      heading: 'Pricing schedule',
      text: "Unit price $81.80 per fixture, Pillar collects in Shenzhen. Ocean freight and handling to the Dallas, Phoenix and Hayward DCs is $2.80 per fixture. Duties and tariffs are for the buyer's account. At the current 30% additional US tariff rate the tariff adds $24.54 per fixture. No other fees are quoted.",
    },
    {
      ref: '3',
      heading: 'Logistics',
      text: 'Lead time is 11 weeks by ocean freight. Minimum order quantity is 10,500 fixtures, in full container loads of 3,500. Brightpak offers to hold buffer stock in Shenzhen at Pillar expense.',
    },
    {
      ref: '4',
      heading: 'Quality and certifications',
      text: 'ISO 9001:2015 certified. UL certification and DLC Premium qualification for the 150 W, 5000K model have been applied for and are not yet held, with certificates expected in the first quarter of 2027. The light lifetime (hours until output falls to 70%) is 60,000 hours with a 5-year warranty. Brightpak offers 30 days of written notice before changes of site, LED package, driver or housing material. Performance as a one-off supplier to Pillar: 88.5% on time and in full and 410 defects per million.',
    },
    {
      ref: '5',
      heading: 'Capacity',
      text: 'Capacity is 169,000 fixtures per year. Pillar at 100% would use about 77% of the line.',
    },
    {
      ref: '6',
      heading: 'Sustainability',
      text: 'Sustainability rating (EcoVadis) 44. No recyclable packaging offer. Recycled aluminium content in housings is 5%. A forced-labor attestation is provided for Brightpak itself only, and names of the suppliers behind its aluminium housings are available on request.',
    },
    {
      ref: '7',
      heading: 'Terms and exceptions',
      text: 'Payment Net 30. Price validity 60 days from the bid due date. Prices are held for 12 months, and any change in tariff rate is passed through to Pillar immediately. Exceptions to Pillar terms: payment terms, price validity, notice before changes and certification requirements.',
    },
    {
      ref: '8',
      heading: 'Company information',
      text: `${getSupplier('sup-brightpak').name}, Shenzhen, China. Supplier to Pillar on one-off purchase orders since ${getSupplier('sup-brightpak').supplierSince ?? 2023}. Commercial contact: ${contactLine('sup-brightpak')}. Business ID (D-U-N-S) ${getSupplier('sup-brightpak').duns}.`,
    },
  ],
};

export const BID_DOCUMENTS: AskDocument[] = [
  RFP_DOCUMENT,
  AURELIA_BID,
  NORTHGATE_BID,
  HANOVER_BID,
  BRIGHTPAK_BID,
];
